import type {
  ReportAggregationPage,
  ReportBar,
  ReportConditionsBlock,
  ReportEconomyBlock,
  ReportPositionScale,
  ReportScaleMarker,
  ReportSnapshot,
  ReportStatTile,
  ReportTestPage,
  ReportVehicleColumn,
} from 'fuel-benchmark-contracts';
import './report-document.css';

export interface ReportDocumentProps {
  snapshot: ReportSnapshot;
}

/**
 * The printed executive brief (plan §8), rendered **entirely** from the saved snapshot: the cover, the test
 * aggregation page, one page per included **test**, and the measured results last — each on its own A4 sheet.
 * Nothing here computes, rounds, translates or localises a thing; every string on the page was frozen when
 * the report was generated, which is what makes a saved report reproducible. The printed page count is
 * `2 + snapshot.testPages.length + 1`.
 *
 * `dir="ltr"` is set explicitly: the builder's chrome follows the app's language (including RTL Arabic), but
 * the document itself is an English print artefact and always reads left to right.
 */
export const ReportDocument = ({ snapshot }: ReportDocumentProps) => {
  const { page1, aggregation, results } = snapshot;
  const resultsPageNumber = 2 + snapshot.testPages.length + 1;

  return (
    <div
      className="report-doc"
      dir="ltr"
      lang="en"
      data-report-id={snapshot.reportId ?? 'draft'}
      data-test-count={snapshot.tests.length}
    >
      <section
        className="report-page"
        data-report-page="1"
      >
        {/* The wordmark row is the whole header: no kicker, no dateline (post-MVP edit 6). */}
        <header className="report-header">
          <p className="report-wordmark">{page1.header.wordmark}</p>
        </header>

        <h1 className="report-title">{page1.title}</h1>
        <p className="report-subtitle">{page1.subtitle}</p>

        {/* No hero tiles: the cover is the brief, and every figure lives on the pages behind it. */}
        <div className="report-banner">
          <p className="report-banner-label">{page1.takeawayLabel}</p>
          <p className="report-banner-text">{page1.takeaway}</p>
        </div>

        <h2 className="report-heading">{page1.specsHeading}</h2>
        <div className="report-cards">
          {page1.specCards.map((card) => (
            <div
              key={card.vehicleId}
              className="report-card"
              data-vehicle-id={card.vehicleId}
            >
              <div className="report-card-head">
                <div>
                  <p className="report-card-name">{card.vehicleLabel}</p>
                  <p className="report-card-code">{card.vehicleCode}</p>
                </div>
                <p className="report-badge">{card.badge}</p>
              </div>
              <dl className="report-rows">
                {card.rows.map((row) => (
                  <div
                    key={row.label}
                    className="report-row"
                  >
                    <dt className="report-row-label">{row.label}</dt>
                    <dd className="report-row-value">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </section>

      <AggregationPage page={aggregation} />

      {snapshot.testPages.map((page, index) => (
        <TestPage
          key={page.key}
          page={page}
          pageNumber={index + 3}
        />
      ))}

      <section
        className="report-page"
        data-report-page={resultsPageNumber}
      >
        <SectionHeading heading={results.section} />

        <h2 className="report-heading">{results.resultsHeading}</h2>
        {results.blocks.map((block) => (
          <EconomyBlock
            key={block.key}
            block={block}
          />
        ))}

        {results.why ? (
          <>
            <h2 className="report-heading">{results.why.heading}</h2>
            <div className="report-why">
              <div className="report-tiles report-tiles-single">
                {results.why.tiles.map((tile) => (
                  <StatTile
                    key={tile.key}
                    tile={tile}
                  />
                ))}
              </div>
              <div className="report-bars">
                <p className="report-bars-caption">{results.why.barsCaption}</p>
                {results.why.bars.map((bar) => (
                  <Bar
                    key={bar.vehicleId}
                    bar={bar}
                  />
                ))}
              </div>
            </div>
          </>
        ) : null}

        {/* Heading included: a report saved with no conclusions prints no conclusions section at all. */}
        {results.conclusions ? (
          <>
            <h2 className="report-heading">{results.conclusions.heading}</h2>
            {results.conclusions.items.map((conclusion) => (
              <div
                key={conclusion.number}
                className="report-conclusion"
              >
                <p className="report-conclusion-number">{conclusion.number}</p>
                <p className="report-conclusion-text">{conclusion.text}</p>
              </div>
            ))}
          </>
        ) : null}

        {/* The document's only small print, and only when every run agreed on the method. */}
        {results.footer ? (
          <footer className="report-footer">
            <p>{results.footer.fuelMethod}</p>
          </footer>
        ) : null}
      </section>
    </div>
  );
};

/**
 * The aggregation page — the benchmark sheet, and since the situation-matrix edit a single table: one row per
 * included test, labelled by what makes that test different from the others, one column per compared vehicle,
 * and the mean result where they meet with the lowest of each row in bold. One segment scale follows beneath it —
 * averaged over the report's drive tests, so it is headed by no test — and an idle-only brief carries none.
 */
const AggregationPage = ({ page }: { page: ReportAggregationPage }) => (
  <section
    className="report-page"
    data-report-page="2"
  >
    <SectionHeading heading={page.section} />

    <h2 className="report-heading">{page.matrix.heading}</h2>
    {page.matrix.caption ? <p className="report-matrix-note">{page.matrix.caption}</p> : null}

    <div className="report-table-wrap">
      <table className="report-table report-matrix report-situation">
        <thead>
          <tr>
            <th scope="col">{page.matrix.measureLabel}</th>
            {page.matrix.columns.map((column) => (
              <th
                key={column.vehicleId}
                scope="col"
                className="report-num"
                data-vehicle-id={column.vehicleId}
              >
                <VehicleColumnHead column={column} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {page.matrix.rows.map((row) => (
            <tr
              key={row.testKey}
              data-test-key={row.testKey}
            >
              <th
                scope="row"
                className="report-situation-head"
              >
                <span className="report-situation-chip">{row.testLabel}</span>
                <span className="report-situation-text">
                  <span className="report-situation-label">{row.label}</span>
                  <span className="report-situation-sub">{row.subLabel}</span>
                </span>
              </th>
              {row.cells.map((cell) => (
                <td
                  key={cell.vehicleId}
                  className={`report-num${cell.isBest ? ' report-strong-cell' : ''}`}
                  data-vehicle-id={cell.vehicleId}
                >
                  {cell.display}
                  {/* Only ever set when the cell averages more than one run. */}
                  {cell.runsDisplay ? <span className="report-cell-runs">{cell.runsDisplay}</span> : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {page.scale ? <PositionScale scale={page.scale} /> : null}
  </section>
);

/**
 * The segment position scale: positioned divs on a rule, no images and no script, so it prints exactly as it
 * renders. The best figure is the left-hand end, because lower consumption is better. Its caption says what the
 * markers are an average of — the renderer prints the snapshot's sentence and counts nothing itself.
 */
const PositionScale = ({ scale }: { scale: ReportPositionScale }) => (
  <div className="report-scale">
    <p className="report-scale-heading">{scale.heading}</p>
    <div className="report-scale-plot">
      <div className="report-scale-track" />
      {scale.markers.map((marker) => (
        <Marker
          key={marker.vehicleId}
          marker={marker}
        />
      ))}
    </div>
    <div className="report-scale-ends">
      <span>{scale.minDisplay}</span>
      <span>{scale.maxDisplay}</span>
    </div>
    <p className="report-scale-caption">{scale.caption}</p>
  </div>
);

const Marker = ({ marker }: { marker: ReportScaleMarker }) => (
  <div
    className={`report-scale-marker${marker.isCompared ? ' is-compared' : ' is-peer'}${marker.isBest ? ' is-best' : ''}`}
    style={{ left: `${marker.pct}%` }}
    data-vehicle-id={marker.vehicleId}
  >
    <span className="report-scale-value">{marker.display}</span>
    <span className="report-scale-label">{marker.label}</span>
    <span className="report-scale-tick" />
  </div>
);

const ConditionsBlock = ({ conditions }: { conditions: ReportConditionsBlock }) => (
  <div className="report-conditions">
    <dl className="report-conditions-rows">
      {conditions.rows.map((row) => (
        <div
          key={row.key}
          className="report-conditions-row"
        >
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
    <p className="report-conditions-verdict">
      <span>{conditions.verdict.label}:</span>
      <span
        className={outcomeClass(
          conditions.verdict.verdict === 'COMPARABLE' ? 'PASS' : conditions.verdict.verdict === 'NOT_COMPARABLE' ? 'BLOCK' : 'WARN',
        )}
      >
        {conditions.verdict.display}
      </span>
    </p>
  </div>
);

/**
 * One included test, on its own sheet: the route it was driven on, every vehicle's readings side by side in
 * one table, and the comparability block for this test alone. A column that averages several runs says so in
 * its own header, so an aggregate can never be read as a single run.
 */
const TestPage = ({ page, pageNumber }: { page: ReportTestPage; pageNumber: number }) => (
  <section
    className="report-page"
    data-report-page={pageNumber}
    data-test-key={page.key}
  >
    <SectionHeading heading={page.section} />

    <h2 className="report-heading">{page.route.heading}</h2>
    <div className="report-test-route">
      {page.route.lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>

    <h2 className="report-heading">{page.readings.heading}</h2>
    <div className="report-table-wrap">
      <table className="report-table report-matrix">
        <thead>
          <tr>
            <th scope="col">{page.readings.measureLabel}</th>
            {page.readings.columns.map((column) => (
              <th
                key={column.vehicleId}
                scope="col"
                className="report-num"
                data-vehicle-id={column.vehicleId}
              >
                {column.label}
                <br />
                <span className="report-col-meta">
                  {column.code} · {column.badge} · {column.runsDisplay}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {page.readings.rows.map((row) => (
            <tr
              key={row.key}
              className={row.emphasis ? 'report-strong' : undefined}
              data-reading={row.key}
            >
              <td>{row.label}</td>
              {row.perVehicle.map((entry) => (
                <td
                  key={entry.vehicleId}
                  className="report-num"
                >
                  {entry.display}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 className="report-heading">{page.conditions.heading}</h2>
    <ConditionsBlock conditions={page.conditions} />
  </section>
);

/** One test's economy verdict on the results page: the same vehicles, stripped back to the money question. */
const EconomyBlock = ({ block }: { block: ReportEconomyBlock }) => (
  <div
    className="report-block"
    data-test-key={block.key}
  >
    <p className="report-block-heading">{block.heading}</p>
    <div className="report-table-wrap">
      <table className="report-table report-matrix">
        <thead>
          <tr>
            <th scope="col">{block.measureLabel}</th>
            {block.columns.map((column) => (
              <th
                key={column.vehicleId}
                scope="col"
                className="report-num"
                data-vehicle-id={column.vehicleId}
              >
                <VehicleColumnHead column={column} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row) => (
            <tr
              key={row.key}
              className={row.emphasis ? 'report-strong' : undefined}
              data-reading={row.key}
            >
              <td>{row.label}</td>
              {row.perVehicle.map((entry) => (
                <td
                  key={entry.vehicleId}
                  className="report-num"
                >
                  {entry.display}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const outcomeClass = (outcome: string): string => (outcome === 'PASS' ? 'report-pass' : outcome === 'WARN' ? 'report-warn' : 'report-block-text');

const StatTile = ({ tile }: { tile: ReportStatTile }) => (
  <div className="report-tile">
    <p className="report-tile-figure">{tile.figure}</p>
    <p className="report-tile-label">{tile.label}</p>
    <p className="report-tile-caption">{tile.caption}</p>
  </div>
);

const VehicleColumnHead = ({ column }: { column: ReportVehicleColumn }) => (
  <>
    {column.label}
    <br />
    <span className="report-col-meta">
      {column.code} · {column.badge}
    </span>
  </>
);

const Bar = ({ bar }: { bar: ReportBar }) => (
  <div className="report-bar">
    <div className="report-bar-head">
      <span>{bar.label}</span>
      <span>{bar.display}</span>
    </div>
    <div className="report-bar-track">
      <div
        className="report-bar-fill"
        style={{ width: `${bar.pct}%` }}
      />
    </div>
  </div>
);

const SectionHeading = ({ heading }: { heading: { number: string; title: string; subtitle: string } }) => (
  <>
    <div className="report-section">
      <span className="report-section-number">{heading.number}</span>
      <span className="report-section-title">{heading.title}</span>
    </div>
    <p className="report-section-subtitle">{heading.subtitle}</p>
  </>
);
